'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: 'pricing' | 'process' | 'comparison' | 'logistics';
}

const FAQ = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqData: FAQItem[] = [
    {
      id: 1,
      category: 'pricing',
      question: 'How can direct sale prices be higher than auction prices?',
      answer: 'Direct sales eliminate the middleman costs that reduce auction values. Auctions charge buyer fees (8-15%), seller commissions (3-8%), and often result in below-market sales due to competitive bidding dynamics. We pay fair market value directly, without these additional costs eating into your return. Our business model is built on volume and efficiency, not maximizing margins on individual transactions.'
    },
    {
      id: 2,
      category: 'process',
      question: 'How quickly can you evaluate and purchase my fleet?',
      answer: 'Most fleet evaluations are completed within 48 hours of initial contact. We can conduct on-site assessments at your facility, provide firm offers within 24-48 hours of evaluation, and complete the entire transaction in 7-14 days on average. For urgent situations, we can expedite the process even further. Payment is made via wire transfer on the day of vehicle pickup.'
    },
    {
      id: 3,
      category: 'comparison',
      question: 'What if my vehicles don\'t sell at auction?',
      answer: 'This is a critical risk that many fleet managers overlook. Auction sales are never guaranteed - if bidding doesn\'t meet reserve prices, vehicles remain unsold. You\'ve already paid transport costs, listing fees, and lost valuable time. With direct sale, you receive a firm, guaranteed offer upfront. There\'s zero risk of non-sale, and you know exactly what you\'ll receive before committing.'
    },
    {
      id: 4,
      category: 'logistics',
      question: 'Do I need to transport vehicles to your location?',
      answer: 'No. We come to you. Our team conducts all evaluations at your facility, eliminating transport costs and logistics headaches. We handle all pickup arrangements and can work around your operational schedule to minimize disruption. This is another significant cost savings compared to auction, where you must transport vehicles to auction sites at your expense.'
    },
    {
      id: 5,
      category: 'pricing',
      question: 'Are there any hidden fees or costs?',
      answer: 'Absolutely none. The price we offer is the price you receive. No buyer fees, no seller commissions, no listing charges, no transport costs, no documentation fees. What you see is what you get. This transparency is a core principle of our business and a major advantage over auction channels where fees can reduce your net proceeds by 15-20% or more.'
    },
    {
      id: 6,
      category: 'process',
      question: 'What documentation do you handle?',
      answer: 'We handle all transaction documentation including title transfers, bill of sale, lien releases (if applicable), and any state-specific requirements. Our team manages the entire paperwork process, ensuring compliance and accuracy. You simply need to provide the titles and any existing documentation - we take care of the rest.'
    },
    {
      id: 7,
      category: 'comparison',
      question: 'Can I still get multiple offers like at auction?',
      answer: 'While auction creates competition among buyers, it also creates downward price pressure and uncertainty. Our offers are based on comprehensive market analysis and fair market value, typically exceeding what you\'d net after auction fees. However, you\'re always free to get multiple direct sale quotes. We\'re confident our offers will be competitive, and you\'ll appreciate the certainty and speed of our process.'
    },
    {
      id: 8,
      category: 'logistics',
      question: 'What condition do vehicles need to be in?',
      answer: 'We purchase vehicles in various conditions - from excellent to fair. Unlike auctions where condition significantly impacts bidding, we provide fair offers based on realistic market values for each vehicle\'s actual state. We\'re transparent about how condition affects pricing and don\'t require vehicles to be detailed or prepared beyond normal operational status.'
    }
  ];

  const categories = [
    { key: 'pricing', label: 'Pricing & Value', icon: 'CurrencyDollarIcon' },
    { key: 'process', label: 'Process & Timeline', icon: 'ClockIcon' },
    { key: 'comparison', label: 'Auction Comparison', icon: 'ScaleIcon' },
    { key: 'logistics', label: 'Logistics & Documentation', icon: 'TruckIcon' }
  ];

  const toggleFAQ = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 md:py-24 bg-muted">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-card rounded w-1/3"></div>
            <div className="h-4 bg-card rounded w-2/3"></div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-card rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 bg-muted">
      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="QuestionMarkCircleIcon" size={20} variant="outline" className="text-primary" />
            <span className="text-sm font-semibold text-primary">Common Questions</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold font-headline text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-muted-foreground">
            Get answers to the most common questions about choosing direct sale over auction.
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {categories.map((category) => (
            <button
              key={category.key}
              className="px-4 py-2 bg-card text-foreground rounded-md hover:bg-primary hover:text-primary-foreground transition-all duration-200 ease-out flex items-center gap-2 text-sm font-medium"
            >
              <Icon name={category.icon as any} size={16} variant="outline" />
              {category.label}
            </button>
          ))}
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqData.map((faq) => (
            <div
              key={faq.id}
              className="bg-card rounded-lg shadow-brand overflow-hidden transition-all duration-200"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors duration-200"
              >
                <span className="text-base font-semibold text-foreground pr-4">
                  {faq.question}
                </span>
                <Icon
                  name={openId === faq.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={20}
                  variant="outline"
                  className="text-primary flex-shrink-0"
                />
              </button>
              {openId === faq.id && (
                <div className="px-6 pb-4">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-base text-foreground mb-6">
            Still have questions? We're here to help.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button className="w-full sm:w-auto px-8 py-4 bg-cta text-cta-foreground font-semibold rounded-md hover:bg-opacity-90 transition-all duration-200 ease-out hover:-translate-y-0.5 shadow-brand">
              Schedule a Consultation
            </button>
            <button className="w-full sm:w-auto px-8 py-4 bg-card text-foreground font-semibold rounded-md hover:bg-muted transition-all duration-200 ease-out border border-border">
              Call Us: (555) 123-4567
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;